import { ChevronDown, Info, X } from "lucide-react";
import { useState } from "react";
import type { Candidate } from "../data/mock-candidates";

type ScoutSendPanelProps = {
  candidate: Candidate;
  onClose: () => void;
};

function StepIndicator({ current }: { current: 1 | 2 | 3 }) {
  const steps = [
    { num: 1, label: "スカウト文の入力" },
    { num: 2, label: "送信前の確認" },
    { num: 3, label: "送信完了" },
  ] as const;

  return (
    <div className="flex items-center justify-between px-6 py-3">
      {steps.map((step, i) => (
        <div className="flex items-center gap-2" key={step.num}>
          {i > 0 ? <div className="mx-2 h-px w-8 bg-gray-200" /> : null}
          <div
            className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium ${
              step.num === current
                ? "bg-blue-600 text-white"
                : step.num < current
                  ? "bg-blue-100 text-blue-600"
                  : "bg-gray-100 text-gray-400"
            }`}
          >
            {step.num}
          </div>
          <span
            className={`text-xs ${
              step.num === current ? "font-medium text-gray-900" : "text-gray-400"
            }`}
          >
            {step.label}
          </span>
        </div>
      ))}
    </div>
  );
}

function RequiredBadge() {
  return (
    <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-600">
      必須
    </span>
  );
}

export function ScoutSendPanel({ candidate, onClose }: ScoutSendPanelProps) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [subject, setSubject] = useState(
    `【フルフレックス/リモート可】${candidate.jobCategory}ポジションのご案内`,
  );
  const [body, setBody] = useState(
    `${candidate.memberId}様

はじめまして。
株式会社サンプル、採用担当の採用太郎と申します。

${candidate.memberId}様のご経歴を拝見し、ぜひ一度お話しする機会をいただけないかと思い、ご連絡いたしました。

当社では現在、${candidate.jobCategory}のポジションで即戦力となる方を探しております。
${candidate.memberId}様の${candidate.currentCompany || "これまで"}でのご経験は、当社が求める人材像と非常にマッチしていると感じております。

まずはカジュアルな面談からでも構いませんので、ご興味をお持ちいただけましたら、ぜひご返信いただけますと幸いです。

何卒よろしくお願いいたします。`,
  );
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [resendEnabled, setResendEnabled] = useState(true);
  const [resendTiming, setResendTiming] = useState("3");

  const insertText = (text: string) => {
    setBody((prev) => `${prev}${text}`);
  };

  if (step === 3) {
    return (
      <div className="flex h-full flex-col">
        <div className="flex items-center justify-between border-gray-200 border-b px-4 py-3">
          <h2 className="font-medium text-gray-900 text-sm">スカウトを送信</h2>
          <button className="text-gray-400 hover:text-gray-600" onClick={onClose} type="button">
            <X className="h-5 w-5" />
          </button>
        </div>
        <StepIndicator current={3} />
        <div className="flex flex-1 flex-col items-center justify-center gap-4 p-6">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
            <svg
              className="h-8 w-8 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                d="M5 13l4 4L19 7"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
              />
            </svg>
          </div>
          <h3 className="font-medium text-gray-900 text-lg">スカウトを送信しました</h3>
          <p className="text-center text-gray-500 text-sm">
            {candidate.memberId} 様へスカウトメールを送信しました。
            <br />
            返信をお待ちください。
          </p>
          <button
            className="mt-4 rounded bg-blue-600 px-6 py-2 text-sm font-medium text-white hover:bg-blue-700"
            onClick={onClose}
            type="button"
          >
            閉じる
          </button>
        </div>
      </div>
    );
  }

  if (step === 2) {
    return (
      <div className="flex h-full flex-col">
        <div className="flex items-center justify-between border-gray-200 border-b px-4 py-3">
          <h2 className="font-medium text-gray-900 text-sm">スカウトを送信</h2>
          <button className="text-gray-400 hover:text-gray-600" onClick={onClose} type="button">
            <X className="h-5 w-5" />
          </button>
        </div>
        <StepIndicator current={2} />
        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-4">
            <div>
              <div className="text-xs font-medium text-gray-500">送信先</div>
              <div className="mt-1 text-sm text-gray-900">{candidate.memberId}</div>
            </div>
            <div>
              <div className="text-xs font-medium text-gray-500">件名</div>
              <div className="mt-1 text-sm text-gray-900">{subject}</div>
            </div>
            <div>
              <div className="text-xs font-medium text-gray-500">本文</div>
              <div className="mt-1 whitespace-pre-wrap rounded border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
                {body}
              </div>
            </div>
            {resendEnabled ? (
              <div>
                <div className="text-xs font-medium text-gray-500">再送信</div>
                <div className="mt-1 text-sm text-gray-700">{resendTiming}営業日後に再送信</div>
              </div>
            ) : null}
          </div>
        </div>
        <div className="border-gray-200 border-t px-4 py-3">
          <div className="flex items-center justify-between">
            <button
              className="rounded border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50"
              onClick={() => setStep(1)}
              type="button"
            >
              戻る
            </button>
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-500">
                プラチナスカウト残数: <span className="font-bold text-gray-900">127</span>通
              </span>
              <button
                className="rounded bg-orange-600 px-6 py-2 text-sm font-medium text-white hover:bg-orange-700"
                onClick={() => setStep(3)}
                type="button"
              >
                送信する
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {/* ヘッダー */}
      <div className="flex items-center justify-between border-gray-200 border-b px-4 py-3">
        <h2 className="font-medium text-gray-900 text-sm">スカウトを送信</h2>
        <button className="text-gray-400 hover:text-gray-600" onClick={onClose} type="button">
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* ステップ */}
      <StepIndicator current={1} />

      {/* フォーム */}
      <div className="flex-1 overflow-y-auto border-gray-200 border-t">
        <div className="space-y-5 p-4">
          {/* 求人 */}
          <div>
            <div className="mb-1.5 flex items-center gap-2">
              <RequiredBadge />
              <span className="text-sm font-medium text-gray-800">求人</span>
            </div>
            <div className="rounded border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
              {candidate.jobCategory}ポジション — リモート可・フレックス
              <span className="ml-2 cursor-pointer text-blue-600 text-xs hover:underline">
                変更
              </span>
            </div>
          </div>

          {/* スカウトテンプレート */}
          <div>
            <div className="mb-1.5 text-sm font-medium text-gray-800">スカウトテンプレート</div>
            <div className="relative">
              <select className="w-full appearance-none rounded border border-gray-300 bg-white py-2 pr-8 pl-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500">
                <option>標準スカウトテンプレート</option>
                <option>エンジニア向けテンプレート</option>
                <option>マネージャー向けテンプレート</option>
                <option>カジュアル面談テンプレート</option>
              </select>
              <ChevronDown className="pointer-events-none absolute top-2.5 right-2.5 h-4 w-4 text-gray-400" />
            </div>
            <div className="mt-2 flex items-center gap-2 rounded bg-blue-50 px-3 py-2">
              <Info className="h-4 w-4 shrink-0 text-blue-500" />
              <span className="text-xs text-blue-700">
                別の求人のテンプレートをコピーしてご利用いただけます
              </span>
              <button
                className="ml-auto shrink-0 text-xs font-medium text-blue-600 hover:underline"
                type="button"
              >
                + 選択
              </button>
            </div>
          </div>

          {/* 件名 */}
          <div>
            <div className="mb-1.5 flex items-center gap-2">
              <RequiredBadge />
              <span className="text-sm font-medium text-gray-800">件名</span>
            </div>
            <input
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              onChange={(e) => setSubject(e.target.value)}
              type="text"
              value={subject}
            />
          </div>

          {/* スカウト本文 */}
          <div>
            <div className="mb-1.5 flex items-center gap-2">
              <RequiredBadge />
              <span className="text-sm font-medium text-gray-800">スカウト本文</span>
            </div>
            <div className="mb-2 flex flex-wrap gap-2">
              <button
                className="rounded border border-blue-300 bg-white px-3 py-1 text-xs text-blue-600 hover:bg-blue-50"
                onClick={() => insertText(`{{求職者名}}`)}
                type="button"
              >
                求職者名の挿入
              </button>
              <button
                className="rounded border border-blue-300 bg-white px-3 py-1 text-xs text-blue-600 hover:bg-blue-50"
                onClick={() => insertText(`{{ポジション名}}`)}
                type="button"
              >
                ポジション名（表示用）の挿入
              </button>
              <button
                className="rounded border border-blue-300 bg-white px-3 py-1 text-xs text-blue-600 hover:bg-blue-50"
                onClick={() => insertText(`{{在籍企業名}}`)}
                type="button"
              >
                在籍企業名の挿入
              </button>
            </div>
            <textarea
              className="h-48 w-full resize-y rounded border border-gray-300 px-3 py-2 text-sm leading-relaxed focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              onChange={(e) => setBody(e.target.value)}
              value={body}
            />
          </div>

          {/* 送信予約 */}
          <div>
            <div className="mb-2 text-sm font-medium text-gray-800">送信予約</div>
            <label className="flex items-center gap-2 text-sm">
              <input
                checked={scheduleEnabled}
                className="accent-blue-600"
                onChange={(e) => setScheduleEnabled(e.target.checked)}
                type="checkbox"
              />
              <span className="text-gray-700">送信予約する</span>
            </label>
            {scheduleEnabled ? (
              <div className="mt-2 rounded border border-gray-200 bg-gray-50 p-3 text-xs text-gray-500">
                <p>・翌営業日（土日祝日を除いた平日）の9時〜16時にスカウトを自動送信します</p>
                <p>・スカウト残数は予約した時点で反映されます</p>
              </div>
            ) : null}
          </div>

          {/* 再送信 */}
          <div>
            <div className="mb-2 text-sm font-medium text-gray-800">再送信</div>
            <label className="flex items-center gap-2 text-sm">
              <input
                checked={resendEnabled}
                className="accent-blue-600"
                onChange={(e) => setResendEnabled(e.target.checked)}
                type="checkbox"
              />
              <span className="text-gray-700">再送信を行う</span>
            </label>
            {resendEnabled ? (
              <div className="mt-2">
                <div className="mb-1 text-xs font-medium text-gray-600">再送タイミング</div>
                <div className="flex items-center gap-4">
                  {["3", "5", "10"].map((val) => (
                    <label className="flex items-center gap-1.5 text-sm" key={val}>
                      <input
                        checked={resendTiming === val}
                        className="accent-blue-600"
                        name="resendTiming"
                        onChange={() => setResendTiming(val)}
                        type="radio"
                      />
                      <span>{val}営業日後</span>
                    </label>
                  ))}
                </div>
                <div className="mt-2 text-xs text-gray-500">
                  <p>・スカウトの返信（応募または辞退）が無い求職者へ自動配信します</p>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* フッター */}
      <div className="border-gray-200 border-t px-4 py-3">
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500">
            プラチナスカウト残数: <span className="font-bold text-gray-900">127</span>通
          </span>
          <button
            className="rounded bg-orange-600 px-6 py-2 text-sm font-medium text-white hover:bg-orange-700"
            onClick={() => setStep(2)}
            type="button"
          >
            内容を確認
          </button>
        </div>
      </div>
    </div>
  );
}
